import { Box, Button, TextField, type DialogProps } from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { object, string } from 'yup';
import { useCreateFont } from '~/api/fonts';
import {
  Dialog,
  DialogContentTitle,
  DialogForm,
  DialogTitle,
} from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { toDataUri } from '~/lib/file';
import { i18n } from '~/lib/i18n';

const validationSchema = object({
  fileUri: string()
    .required(i18n.t('pages.fonts.new_dialog.validation.font_required'))
    .label('Font'),
  name: string()
    .matches(/^[a-z ]+$/i, i18n.t('pages.fonts.new_dialog.validation.letters_only'))
    .required(i18n.t('common.validation.name_required'))
    .trim()
    .label('Name'),
});

export const NewFontDialog = (props: DialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const navigate = useNavigate();
  const link = useLink();

  const [createFont] = useCreateFont();
  const [selectedFont, setSelectedFont] = useState<File>();

  const formik = useFormik({
    initialValues: { fileUri: '', name: '' },
    onSubmit: async (values) => {
      const { data } = await createFont({
        variables: {
          input: { ...validationSchema.cast(values), networkId: currentNetwork.id },
        },
      });
      if (data?.createFont?.font.id) {
        navigate(link(`/fonts/${data.createFont.font.id}`));
      }
    },
    validationSchema,
    validateOnChange: true,
  });

  const { getInputProps, open: selectFile } = useDropzone({
    accept: 'font/*, .ttf, .otf, .woff, .woff2', // maybe more?
    multiple: false,
    onDrop: async (accepted, _rejected, _event) => {
      if (accepted.length !== 1) return;
      setSelectedFont(accepted[0]);
      const fileUri = await toDataUri(accepted[0]);
      await formik.setFieldValue('fileUri', fileUri, true);
    },
  });

  return (
    <Dialog {...props}>
      <DialogTitle onClose={() => props.onClose?.({}, 'backdropClick')}>
        {t('pages.fonts.new_dialog.title')}
      </DialogTitle>

      <DialogContent>
        <DialogContentTitle>{t('pages.fonts.new_dialog.description')}</DialogContentTitle>

        <DialogForm id="new-font" onSubmit={formik.handleSubmit}>
          <label>
            <span className="label required">{t('common.labels.name')}</span>
            <TextField
              autoFocus
              error={!!formik.errors.name}
              fullWidth
              helperText={formik.errors.name || ''}
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
            />
          </label>
          <Box sx={{ alignItems: 'center', display: 'flex', '& label': { flexBasis: '25%' } }}>
            <label>
              <span className="label required">{t('pages.fonts.new_dialog.labels.font')}</span>
            </label>
            <Box>
              <input {...getInputProps()} />
              <Button onClick={selectFile} variant="outlined">
                {t('pages.fonts.new_dialog.actions.upload_font')}
              </Button>
              {selectedFont && <Box>{selectedFont.name}</Box>}
            </Box>
          </Box>
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button
          disabled={formik.isSubmitting}
          onClick={() => props.onClose?.({}, 'backdropClick')}
          variant="outlined"
        >
          {t('common.actions.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={!formik.dirty || formik.isSubmitting || !formik.isValid}
          form="new-font"
          type="submit"
          variant="contained"
        >
          {t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
